import { memo, ReactNode } from 'react';
import { Header, Link } from '@amzn/awsui-components-core';

import { useContentMetadataViewModel } from './ContentMetadataViewModel';
import styles from './ContentMetadataView.module.scss';

/**
 * Read-only content metadata table shown at the bottom of the view page:
 * authoring, review assignments, technologies, AWS services, environment, and
 * version. Rows with no data are omitted entirely.
 */
export const ContentMetadataView = memo(function ContentMetadataView() {
  const vm = useContentMetadataViewModel();

  if (!vm) return null;

  // Each row is included only when its value is present, so unset metadata
  // leaves no empty rows behind.
  const items: { label: string; value: ReactNode }[] = [];
  if (vm.authoring) items.push({ label: 'Authoring', value: vm.authoring });
  if (vm.contentBarRaisers)
    items.push({ label: 'Content bar raiser(s)', value: vm.contentBarRaisers });
  if (vm.technicalWriting) items.push({ label: 'Technical writing', value: vm.technicalWriting });
  if (vm.mainTechnology) items.push({ label: 'Main technology', value: vm.mainTechnology });
  if (vm.additionalTechnologies)
    items.push({ label: 'Additional technologies', value: vm.additionalTechnologies });
  if (vm.awsServices) items.push({ label: 'AWS services', value: vm.awsServices });
  if (vm.environment) items.push({ label: 'Environment', value: vm.environment });

  // The version row carries a trailing "View previous versions" link; it is
  // shown only when a version label exists.
  if (vm.version) {
    items.push({
      label: 'Version',
      value: (
        <span className={styles.version}>
          <span>{vm.version}</span>
          <span aria-hidden="true" className={styles.versionDivider}>
            |
          </span>
          <Link variant="primary" onFollow={vm.viewPreviousVersions}>
            View previous versions
          </Link>
        </span>
      ),
    });
  }

  // Nothing to show when the content carries no metadata at all.
  if (items.length === 0) return null;

  return (
    <section className={styles.section}>
      <div className={styles.heading}>
        <Header variant="h2">{vm.heading}</Header>
      </div>
      <dl className={styles.table}>
        {items.map((item) => (
          <div className={styles.row} key={item.label}>
            <dt className={styles.label}>{item.label}</dt>
            <dd className={styles.value}>{item.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
});
